// Deliverable Create/Edit dialog. Owns state + validation; delegates
// rendering to DeliverableFormFields. No "Renew" mode (specific to
// insurance). Recurrence fields validated via isoDateValidation so
// garbage dates don't reach the backend. Uses toUpdateDeliverableInput on
// the edit path so mode-gated fields are omitted from the payload rather
// than silently cleared. In edit mode, renders the lifecycle log editor
// via DeliverableFormFields; when lifecycleEventsReplace is present in
// state the save path sends that field and omits status and lifecycleEvents.

import { useState } from "react";
import Modal from "../../../common/Modal";
import Button from "../../../common/Button";
import Alert from "../../../common/Alert";
import DeliverableFormFields from "./DeliverableFormFields";
import {
  buildEmptyDeliverableFormState,
  buildSowIdUpdatePatch,
  stateFromDeliverable,
  toCreateDeliverableInput,
  toUpdateDeliverableInput,
  type DeliverableFieldErrors,
  type DeliverableFormState,
} from "./deliverableFormState";
import { isValidIsoDate } from "../shared/isoDateValidation";
import { validateLifecycleChain } from "./lifecycleTransitions";
import useSeededFormState from "../../../../hooks/useSeededFormState";
import type {
  CreateDeliverableInput,
  Deliverable,
  Milestone,
  Sow,
  UpdateDeliverableInput,
} from "../../../../types";

export type DeliverableFormMode = "create" | "edit";

interface DeliverableFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: DeliverableFormMode;
  contractId: string;
  source?: Deliverable;
  milestones?: Milestone[];
  sows?: Sow[];
  /** Existing clause-references on this contract for the typeahead. */
  clauseSuggestions?: string[];
  /** Pre-fill `sowId` on Create. Used by the cross-tab "Add deliverable
   *  to this SOW" flow from SowDetailExpand. Ignored on Edit. */
  initialSowId?: string;
  onCreate?: (input: CreateDeliverableInput) => Promise<unknown>;
  onUpdate?: (input: UpdateDeliverableInput) => Promise<unknown>;
  isSubmitting?: boolean;
  /** Effective end date of the parent contract (admin-maintained).
   *  Threaded through to DeliverableFormFields for Task 7's helper text
   *  and serialiser auto-fill. */
  contractEndDate?: string;
}

function validate(state: DeliverableFormState): DeliverableFieldErrors {
  const errors: DeliverableFieldErrors = {};
  if (!state.name.trim()) errors.name = "Name is required";
  if (state.dueDate && !isValidIsoDate(state.dueDate)) {
    errors.dueDate = "Enter a valid due date";
  }
  if (state.recurrenceRule) {
    if (!isValidIsoDate(state.recurrenceRule.startDate)) {
      errors.recurrenceStartDate = "Enter a valid recurrence start date";
    }
    if (
      state.recurrenceRule.endDate &&
      !isValidIsoDate(state.recurrenceRule.endDate)
    ) {
      errors.recurrenceEndDate = "Enter a valid recurrence end date";
    }
    if (
      !errors.recurrenceStartDate &&
      !errors.recurrenceEndDate &&
      state.recurrenceRule.endDate &&
      state.recurrenceRule.endDate < state.recurrenceRule.startDate
    ) {
      errors.recurrenceEndDate = "End date must be on or after start date";
    }
  }
  // Validate the lifecycle chain when the editor is active.
  if (state.lifecycleEventsReplace !== undefined) {
    const chain = validateLifecycleChain(state.lifecycleEventsReplace);
    if (!chain.ok) {
      errors.lifecycleChain = chain.error;
    }
  }
  return errors;
}

const modeTitle: Record<DeliverableFormMode, string> = {
  create: "Add deliverable / obligation",
  edit: "Edit deliverable / obligation",
};

export default function DeliverableFormModal({
  isOpen,
  onClose,
  mode,
  contractId,
  source,
  milestones,
  sows,
  clauseSuggestions,
  initialSowId,
  onCreate,
  onUpdate,
  isSubmitting,
  contractEndDate,
}: DeliverableFormModalProps) {
  const [errors, setErrors] = useState<DeliverableFieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Re-seed whenever the modal opens or its target changes (different source
  // row or switched mode). The modal is eager-mounted (only isOpen toggles) —
  // useSeededFormState resets its guard on close, so reopening with the same
  // key (e.g. create → create) re-seeds instead of showing the previous
  // values.
  const [state, setState] = useSeededFormState<DeliverableFormState>(
    isOpen,
    `${mode}:${source?.id ?? "new"}:${initialSowId ?? ""}`,
    () => {
      if (mode === "edit" && source) {
        const formState = stateFromDeliverable(source);
        // Seed the lifecycle log editor for every non-recurring deliverable —
        // the LifecycleLogEditor is now the uniform edit surface and the
        // legacy bare-`status` write path is gone. Log-less rows (brand-new
        // not-started) seed an empty array: the replace path recomputes
        // derived state from it, with nothing to lose (a not-started row has
        // no stamped timestamps). Recurring parents have no lifecycle of
        // their own.
        if (!source.recurrenceRule) {
          formState.lifecycleEventsReplace = [
            ...(source.lifecycleEvents ?? []),
          ];
        }
        return formState;
      }
      const empty = buildEmptyDeliverableFormState();
      if (initialSowId) empty.sowId = initialSowId;
      return empty;
    },
    () => {
      setErrors({});
      setSubmitError(null);
    },
  );

  const handleChange = (patch: Partial<DeliverableFormState>) => {
    setState((prev) => ({ ...prev, ...patch }));
    // Clear related error when user edits that field
    const keys = Object.keys(patch);
    setErrors((prev) => {
      const next = { ...prev };
      for (const k of keys) {
        delete next[k as keyof DeliverableFieldErrors];
      }
      if (keys.includes("recurrenceRule")) {
        delete next.recurrenceStartDate;
        delete next.recurrenceEndDate;
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate(state);
    if (Object.keys(v).length > 0) {
      setErrors(v);
      return;
    }
    setSubmitError(null);
    try {
      if (mode === "edit" && source && onUpdate) {
        // isRecurringParent mirrors DeliverableFormFields' heuristic so
        // mode-gated fields (ragRating, status, etc.) are omitted from the
        // payload rather than sent as empty strings.
        const isRecurringParent = !!state.recurrenceRule;
        let input: UpdateDeliverableInput;
        if (state.lifecycleEventsReplace !== undefined) {
          // Lifecycle-replace path: send ONLY lifecycleEventsReplace +
          // non-status fields. Backend rejects requests that send both
          // lifecycleEventsReplace and status in the same body.
          const base = toUpdateDeliverableInput(state, source, isRecurringParent);
          // Strip status and lifecycleEvents (delta) — mutually exclusive with replace.
          const { status: _s, lifecycleEvents: _lc, ...rest } = base;
          void _s; void _lc;
          input = {
            version: source.version,
            contractId,
            ...rest,
            ...buildSowIdUpdatePatch(state, source),
            lifecycleEventsReplace: state.lifecycleEventsReplace,
          };
        } else {
          input = {
            version: source.version,
            contractId,
            ...toUpdateDeliverableInput(state, source, isRecurringParent),
            ...buildSowIdUpdatePatch(state, source),
          };
        }
        await onUpdate(input);
      } else if (mode === "create" && onCreate) {
        await onCreate(toCreateDeliverableInput(state, contractId, { contractEndDate }));
      }
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to save deliverable.";
      // 409 version conflict — surface a reload prompt rather than a generic error.
      if (msg.toLowerCase().includes("version conflict") || msg.includes("409")) {
        setSubmitError(
          "This deliverable was modified by another user. Reload and try again.",
        );
      } else {
        setSubmitError(msg);
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modeTitle[mode]}
      maxWidth="sm:max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <DeliverableFormFields
          value={state}
          onChange={handleChange}
          errors={errors}
          idPrefix={`del-${mode}`}
          disabled={isSubmitting}
          milestones={milestones}
          sows={sows}
          clauseSuggestions={clauseSuggestions}
          contractEndDate={contractEndDate}
          isEditMode={mode === "edit"}
          sourceLifecycleEvents={source?.lifecycleEvents}
        />
        {errors.lifecycleChain && (
          <Alert variant="error">{errors.lifecycleChain}</Alert>
        )}
        {submitError && <Alert variant="error">{submitError}</Alert>}
        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="secondary"
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
            {mode === "edit" ? "Save changes" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
